import React, { useEffect, useState, useContext, useRef } from 'react';
import { TooltipFormatterContextObject, AxisLabelsFormatterContextObject } from 'highcharts';
import { ChartContainer, TableHeading, NcfToggle } from './PowerNcfVolumeChart.style';
import { SortingState, getSortedRowModel } from '@tanstack/react-table';
import {
  groupAndSortDataByRegion,
  generateSeries,
  getAllUniqueDatesOrQuarters,
  IRegionQuarterData,
  tooltipFormatter,
  getCommodityName,
  getTitleText,
  fetchDataBasedOnView,
  generateColumns,
  axisLabelFormatter,
} from './NcfVolumeUtils';
import Chart from '../../../Chart/Chart';
import { BaseTable, Flexbox } from '@sede-enterprise/shell-ds-react-framework';
import { AppContext } from '../../../../../../Context/AppContext';
import { INcfQuarterwise } from '../../../../../../Types/types';
import ChartToggle from '../../../../../ReUsableComponents/PPAToggle/PPAToggle';
import DownloadCsvButton from '../../../../../ReUsableComponents/DownloadCsvButton/DownloadCsvButton';
import { notify } from '../../../../../../../CarbonDashboard/components/Toast/notify';
import isEqual from 'lodash/isEqual';
import { isApiResponseEmpty } from '../../../../../Utils/utils';

interface PowerNcfVolumeChartProps {
  setIsNcfQuarterwiseResponseEmpty: (isEmpty: boolean) => void;
}

const PowerNcfVolumeChart: React.FC<PowerNcfVolumeChartProps> = ({ setIsNcfQuarterwiseResponseEmpty }) => {
  const [view, setView] = useState('Chart');
  const [sorting, setSorting] = useState<SortingState>([]);
  const { state: analyticsState, dispatch: analyticsDispatch } =
    useContext(AppContext).analyticsHome;
  const prevViewRef = useRef(view); // Ref to store the previous value of view
  const prevFilteredValuesRef = useRef(analyticsState.filteredValues);
  const { state: appDataState } = useContext(AppContext).appData;
  const isRegionSpecificUser = Object.values(appDataState.userRegionFlags).some((el) => el);

  useEffect(() => {
    // This triggers for global users
    if (
      !isEqual(prevFilteredValuesRef.current, analyticsState.filteredValues) ||
      prevViewRef.current !== view
    ) {
      fetchData();
      // Update the refs with the current values
      prevFilteredValuesRef.current = analyticsState.filteredValues;
      prevViewRef.current = view;
    }
  }, [view, analyticsDispatch, analyticsState.filteredValues]);

  useEffect(() => {
    // This triggers for a region specific users. Ex. Asia or Australia
    if (!isRegionSpecificUser) {
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetchDataBasedOnView(view, analyticsState);
      if (response) {
        analyticsDispatch({
          type: 'setNcfQuarterwise',
          payload: response.data,
        });
        setIsNcfQuarterwiseResponseEmpty(isApiResponseEmpty(
          response.data,
          analyticsState.filteredValues.region,
          analyticsState.filteredValues.commodity,
        ));

      }
    } catch (error) {
      analyticsDispatch({
        type: 'setNcfQuarterwise',
        payload: [],
      });
      notify('error', error instanceof Error ? error.message : 'Unknown error')
    }
  };

  // Group and sort data by region
  const mapData: INcfQuarterwise[] = analyticsState.ncfQuarterwise;
  const dataByRegion = groupAndSortDataByRegion(mapData);
  const series = generateSeries(dataByRegion);
  let yAxisTitle = `Quantity(${analyticsState?.filteredValues?.uom})`;
  //if single year selected then show cumulative sum
  if (analyticsState?.filteredValues?.year.length === 1) {
    yAxisTitle = `Cumulative Sum(${analyticsState?.filteredValues?.uom})`;
  }

  const options = {
    xAxis: {
      categories: getAllUniqueDatesOrQuarters(dataByRegion as Record<string, IRegionQuarterData[]>),

      title: {
        text: getTitleText(mapData),
      },
      labels: {
        // eslint-disable-next-line no-unused-vars
        formatter(this: AxisLabelsFormatterContextObject) {
          return axisLabelFormatter(this);
        },
      },
    },
    yAxis: {
      title: {
        text: yAxisTitle,
      },
    },
    tooltip: {
      shared: false,
      // eslint-disable-next-line no-unused-vars
      formatter(this: TooltipFormatterContextObject) {
        return tooltipFormatter(this, mapData);
      },
    },
    plotOptions: {
      series: {
        pointWidth: 20,
        pointPadding: 0.2, // Padding between each column or bar, defaults to 0.1
        groupPadding: 0.2, // Padding between each value groups, in x axis units, defaults to 0.2
        dataLabels: {
          enabled: true,
          format: '{point.y:.3f}', // upto 3 decimal points
        },
      },
    },
    series,
  };

  const tableOptions = {
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel()
  };

  return (
    <ChartContainer>
      <Flexbox style={{ width: '100%' }} justifyContent='space-between'>
        <TableHeading>
          {getCommodityName(analyticsState?.filteredValues?.commodity[0])} NCF Volume
        </TableHeading>
        <Flexbox justifyContent='flex-end' alignItems='center' style={{ flex: 1 }}>
          <NcfToggle>
            <ChartToggle setView={setView} />
          </NcfToggle>
          {view === 'Table' && <DownloadCsvButton data={mapData} fileName={'NcfVolume'} />}
        </Flexbox>
      </Flexbox>
      {view === 'Table' ? (
        <BaseTable
          columns={generateColumns(
            analyticsState.filteredValues.region,
            analyticsState.filterOptions.region,
          )}
          data={mapData}
          size='large'
          data-testid='base-table'
          stickyHeader
          maxHeight={383}
          width={'100%'}
          tableOptions={tableOptions as unknown as Object}
        />
      ) : (
        <>
          <Chart options={options} footer={'Source'} />
          <div
            style={{ display: 'none' }}
            data-testid='analytics-power-ncf-volume-chart-hidden-for-automation'
          >
            {JSON.stringify(options.series)}
          </div>
        </>
      )}
    </ChartContainer>
  );
};

export default PowerNcfVolumeChart;